<template>
  <div>
    <d-row no-gutters class="page-header py-4 mb-2">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <template v-if="isInPopup">
          <h4>Diagnozy pacjenta</h4>
        </template>
        <template v-else>
          <h4>Lista wyników diagnozy:
            <p class="mt-1 mb-0 patient-name">
              {{ patient.name + ' ' + patient.last_name }}
            </p>
          </h4>
        </template>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="showAddDiagnosisPopup(patient.id)"
          :disabled="!loaded">
          <i class="material-icons">add</i>
          Dodaj diagnozę
        </d-button>
      </d-col>
    </d-row>
    <table class="table mt-0 mb-0 w-100">
      <thead class="bg-light">
        <tr>
          <th scope="col" class="border-0">Data wystawienia</th>
          <th scope="col" class="border-0">Data wizyty</th>
          <th scope="col" class="border-0">Specjalista</th>
          <th scope="col" class="border-0">Rodzaj diagnozy</th>
          <th scope="col" class="border-0 text-center">Plik diagnozy</th>
          <th scope="col" class="border-0 text-center">Akcje</th>
        </tr>
      </thead>
      <tbody v-if="loaded">
        <tr
          v-for="(diagnosis, diagnosisIndex) of diagnoses"
          :key="'diagnosis-' + diagnosis.id + '-' + diagnosisIndex">
          <td class="align-middle">
            {{diagnosis.created_at.slice(0, 10)}}
          </td>
          <td class="align-middle">
            <template v-if="diagnosis.visit_date !== 'brak...'">
              <p style="color: #17c671">{{ diagnosis.visit_date.slice(0, 11) }}</p>
              <span style="margin-top: -30px; display: block; font-size: 0.9rem; opacity: 0.6">
                {{ diagnosis.visit_date.slice(11, 16) }} - {{ diagnosis.visit_date.slice(11, 16) }}
              </span>
            </template>
            <template v-else>
              <span style="opacity: 0.5">Brak</span>
            </template>
          </td>
          <td class="align-middle">
            {{ getUserName(diagnosis.user_id) }}
          </td>
          <td class="align-middle">
            <template v-if="diagnosis.diagnosis_name !== 'brak...'">
              {{diagnosis.diagnosis_name}}
            </template>
            <template v-else>
              <span style="opacity: 0.5">Brak</span>
            </template>
          </td>
          <td class="align-middle text-center">
            <d-button
              theme="info"
              outline
              :disabled="actionInProgress !== false"
              @click.prevent="downloadDiagnosis(diagnosis.id)">
              <template v-if="actionInProgress !== diagnosis.id">
                <i class="material-icons">cloud_download</i>.
                <span v-if="!isInPopup" class="ml-2">Pobierz</span>
              </template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </d-button>
          </td>
          <td
            class="align-middle text-center"
            style="width: 130px;">
            <d-button
              theme="primary"
              @click.prevent="showEditDiagnosisPopup(diagnosis.id)"
              class="mr-2"
              title="Edytuj">
              <i class="material-icons">edit</i>
            </d-button>
            <d-button
              theme="danger"
              :disabled="removeInProgress"
              @click.prevent="removeDiagnosis(diagnosis.id)"
              title="Usuń">
              <i class="material-icons">delete</i>
            </d-button>
          </td>
        </tr>
        <tr
          v-if="!diagnoses.length">
          <td
            colspan="6"
            class="text-center">
            Brak diagnoz do wyświetlenia&hellip;
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="!loaded" class="pb-3 pt-3 text-center">
      Trwa ładowanie danych&hellip;
    </div>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
    </d-alert>

    <patients-form-diagnosis-popup
      v-if="isShowDiagnosisPopup"
      :on-close-popup="handleCloseDiagnosisPopup"
      :on-submit-callback="loadItemsData"
      :patient-id="patient.id"
      :diagnosis-id="editDiagnosisID || 0"
      :show="true"/>
  </div>
</template>

<script>
import Vue from 'vue';
import ListUtils from '@/utils/ListUtils.js';
import PatientsFormDiagnosisPopup from '@/components/forms/patients/PatientsFormDiagnosisPopup';

export default {
  name: 'patients-form-diagnosis',
  props: {
    allUsers: {
      required: true,
      type: Array
    },
    filterForVisit: {
      default: false,
      type: Boolean
    },
    isInPopup: {
      default: false,
      type: Boolean
    },
    isVisible: {
      default: false,
      type: Boolean
    },
    patient: {
      required: true,
      type: Object
    },
    visitId: {
      defualt: 0,
      type: Number
    }
  },
  components: {
    'patients-form-diagnosis-popup': PatientsFormDiagnosisPopup
  },
  watch: {
    'isVisible': function (newValue) {
      if (newValue && !this.loaded) {
        this.loadItemsData();
      }
    }
  },
  data () {
    return {
      actionInProgress: false,
      diagnoses: [],
      editDiagnosisID: 0,
      isShowDiagnosisPopup: false,
      loaded: false,
      loadError: false,
      removeInProgress: false
    }
  },
  mounted () {
    if (this.isVisible) {
      this.loadItemsData();
    }
  },
  methods: {
    loadItemsData () {
      Vue.set(this, 'loaded', false);
      Vue.set(this, 'loadError', false);

      let where = {};
      if (this.filterForVisit) {
        where.visit_id = this.visitId;
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/patients/result-diagnosis/items?patient_id=' + this.patient.id,
        listField: 'diagnoses',
        pagination: false,
        params: {
          where
        }
      });
    },
    removeDiagnosis (diagnosisID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/patients/result-diagnosis/delete/',
        id: diagnosisID,
        successTitle: 'Usunięto diagnoze',
        successText: 'Wybrana diagnoza został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć tą diagnoze?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie diagnozy nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadItemsData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    downloadDiagnosis (diagnosisID) {
      this.actionInProgress = true;

      this.$http.post('/patients/result-diagnosis/get-file/' + diagnosisID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = 'Diagnoza-nr-';
        link.setAttribute('download', filename + diagnosisID + '.pdf');
        document.body.appendChild(link);
        link.addEventListener('click', this.clickCallback, false);
        link.click();
        document.body.removeChild(link);
        this.downloading = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.downloading = false;
      });
    },
    clickCallback (e) {
      e.stopPropagation();
    },
    getUserName (userID) {
      if (!userID) {
        return 'brak';
      }

      let itemFound = this.allUsers.find(item => item.id === userID);

      if (!itemFound) {
        return 'Nie znaleziono specjalisty';
      }

      return itemFound.name + ' ' + itemFound.last_name;
    },
    showEditDiagnosisPopup (diagnosisID) {
      Vue.set(this, 'editDiagnosisID', diagnosisID);
      Vue.set(this, 'isShowDiagnosisPopup', true);
    },
    showAddDiagnosisPopup () {
      Vue.set(this, 'editDiagnosisID', 0);
      Vue.set(this, 'isShowDiagnosisPopup', true);
    },
    handleCloseDiagnosisPopup () {
      Vue.set(this, 'isShowDiagnosisPopup', false);
      Vue.set(this, 'editDiagnosisID', 0);
    }
  }
}
</script>
